// Toast (DESIGN §3.7.6): sonner, unten mittig (mobil über der Bottom-Nav), Breite bis 420, Höhe 48+,
// Radius 16, `overlay`, `shadow-2`; Icon 20 links (Erfolg CheckCircle `success-fg`, Fehler
// WarningCircle `danger-fg`, Info `accent-fg`), Text `label`, Aktion rechts als Link-Knopf.
// 4 s sichtbar (Fehler 8 s), Hover pausiert, höchstens 3 gestapelt, ⌥T springt in den jüngsten Toast.
import type { ReactNode } from 'react';
import { CheckCircleIcon, InfoIcon, WarningCircleIcon, WarningIcon } from '@phosphor-icons/react';
import { Toaster as SonnerToaster, toast as sonner, type ExternalToast } from 'sonner';
import { cn, Spinner } from '../basis/kern';

export const TOAST_DURATION_MS = 4000;
export const TOAST_ERROR_DURATION_MS = 8000;

export interface ToasterProps {
  /** Abstand unten mobil (über Bottom-Nav 64 bzw. Composer). */
  mobileBottomOffset?: number;
}

/** Sitzt einmal in jeder Shell (basis/shell). */
export function Toaster({ mobileBottomOffset = 80 }: ToasterProps) {
  return (
    <SonnerToaster
      position="bottom-center"
      visibleToasts={3}
      gap={8}
      offset={{ bottom: 24 }}
      mobileOffset={{ bottom: mobileBottomOffset, left: 16, right: 16 }}
      hotkey={['altKey', 'KeyT']}
      containerAriaLabel="Notifications"
      icons={{
        success: (
          <CheckCircleIcon size={20} weight="fill" className="text-success-fg" aria-hidden="true" />
        ),
        error: (
          <WarningCircleIcon
            size={20}
            weight="fill"
            className="text-danger-fg"
            aria-hidden="true"
          />
        ),
        info: <InfoIcon size={20} weight="fill" className="text-accent-fg" aria-hidden="true" />,
        warning: (
          <WarningIcon size={20} weight="fill" className="text-warning-fg" aria-hidden="true" />
        ),
        loading: <Spinner size={20} className="text-accent-fg" />,
      }}
      toastOptions={{
        unstyled: true,
        duration: TOAST_DURATION_MS,
        classNames: {
          toast: cn(
            'flex min-h-12 w-[min(420px,calc(100vw-32px))] items-center gap-3 rounded-xl bg-overlay px-4 py-3 text-fg shadow-2',
            '[[data-theme=light]_&]:border [[data-theme=light]_&]:border-line-subtle',
          ),
          icon: 'inline-flex shrink-0',
          content: 'flex min-w-0 flex-1 flex-col',
          title: 'text-label text-fg',
          description: 'text-small text-fg-secondary',
          actionButton: cn(
            'ml-auto shrink-0 rounded-sm px-1 text-label font-medium text-accent-fg hover:underline',
            'focus-visible:outline-2 focus-visible:outline-focus',
          ),
          cancelButton: 'shrink-0 rounded-sm px-1 text-label text-fg-secondary hover:underline',
        },
      }}
    />
  );
}

export interface ToastOptions {
  description?: ReactNode;
  /** Aktion rechts, z. B. „Undo“ oder „Retry“. */
  action?: { label: string; onClick: () => void };
  id?: string | number;
  duration?: number;
}

const toSonner = (options: ToastOptions = {}, duration = TOAST_DURATION_MS): ExternalToast => ({
  id: options.id,
  description: options.description,
  duration: options.duration ?? duration,
  action: options.action
    ? { label: options.action.label, onClick: () => options.action?.onClick() }
    : undefined,
});

/** Toasts mit den Vorgaben aus DESIGN §3.7.6 (Texte aus UX §7.6). */
export const toast = {
  success: (message: ReactNode, options?: ToastOptions) =>
    sonner.success(message, toSonner(options)),
  error: (message: ReactNode, options?: ToastOptions) =>
    sonner.error(message, toSonner(options, TOAST_ERROR_DURATION_MS)),
  info: (message: ReactNode, options?: ToastOptions) => sonner.info(message, toSonner(options)),
  warning: (message: ReactNode, options?: ToastOptions) =>
    sonner.warning(message, toSonner(options)),
  message: (message: ReactNode, options?: ToastOptions) =>
    sonner.message(message, toSonner(options)),
  loading: (message: ReactNode, options?: ToastOptions) =>
    sonner.loading(message, toSonner(options, Infinity)),
  dismiss: (id?: string | number) => sonner.dismiss(id),
};
